/**
 * Wrapper fetch minimaliste autour de l'API Symfony. Tous les appels :
 *   - Préfixent /api/
 *   - Envoient/reçoivent du JSON
 *   - Incluent les cookies de session (credentials: same-origin)
 *   - Lèvent une ApiError sur HTTP >= 400 (qui contient le code + message)
 *
 * En dev, Vite proxify /api → http://localhost:8000 (cf. vite.config.ts).
 * En prod, Caddy route /api/* vers le container Symfony.
 */

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    public readonly data?: unknown,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

type RequestOptions = {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE'
  body?: unknown
  query?: Record<string, string | number | boolean | undefined | null>
  /** Si true, l'erreur 401 ne sera PAS levée (laisse le router gérer). */
  silentAuth?: boolean
}

const BASE = '/api'

export async function api<T = unknown>(path: string, opts: RequestOptions = {}): Promise<T> {
  let url = BASE + (path.startsWith('/') ? path : '/' + path)
  if (opts.query) {
    const params = new URLSearchParams()
    for (const [k, v] of Object.entries(opts.query)) {
      if (v !== undefined && v !== null) params.append(k, String(v))
    }
    const qs = params.toString()
    if (qs) url += '?' + qs
  }

  const init: RequestInit = {
    method: opts.method ?? 'GET',
    headers: { 'Accept': 'application/json' },
    credentials: 'same-origin',
  }
  if (opts.body !== undefined) {
    init.headers = { ...init.headers, 'Content-Type': 'application/json' }
    init.body = JSON.stringify(opts.body)
  }

  const res = await fetch(url, init)

  // Pas de contenu : OK pour DELETE 204
  if (res.status === 204) return undefined as T

  let payload: unknown
  const ct = res.headers.get('content-type') ?? ''
  if (ct.includes('application/json')) {
    payload = await res.json()
  } else {
    payload = await res.text()
  }

  if (!res.ok) {
    if (res.status === 401 && opts.silentAuth) {
      throw new ApiError(401, 'unauthenticated', payload)
    }
    const msg = (payload as { error?: string } | undefined)?.error ?? `HTTP ${res.status}`
    throw new ApiError(res.status, msg, payload)
  }

  return payload as T
}

// ─── Helpers typés (sucre) ─────────────────────────────────────────

export const apiGet = <T = unknown>(path: string, query?: RequestOptions['query'], silentAuth = false) =>
  api<T>(path, { method: 'GET', query, silentAuth })

export const apiPost = <T = unknown>(path: string, body?: unknown) =>
  api<T>(path, { method: 'POST', body })

export const apiPut = <T = unknown>(path: string, body?: unknown) =>
  api<T>(path, { method: 'PUT', body })

export const apiDelete = <T = unknown>(path: string) =>
  api<T>(path, { method: 'DELETE' })
